import * as React from "react";
import { Button, Checkbox, Modal, Pagination, Table } from "semantic-ui-react";

import "./styles.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppSetting, AppSettingPermission } from "../../../../services/bedrock/dtos/AppSetting";
import { AppSettingServices, AppSettingPermissionUpdate } from "../../../../services/bedrock/AppSettingServices";
import _ from "lodash";
import pluralize from "pluralize";
import { URLS } from "../../../../utils";

interface PlayerPermissionsModalProps {
  setting: AppSetting | undefined;
  withWritePermission: boolean;
  onClose: (didSave: boolean) => void;
}

const PlayerPermissionsModal: React.FC<PlayerPermissionsModalProps> = ({ setting, withWritePermission, onClose }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [permissions, setPermissions] = useState<AppSettingPermission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<{ [key: number]: AppSettingPermission }>({});
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalPermissions, setTotalPermissions] = useState<number>(0);

  useEffect(() => {
    if (!setting) {
      setPermissions([]);
      setSelectedPermissions({});
      setPage(1);
      setTotalPages(0);
      return;
    }

    setLoading(true);
    AppSettingServices.getPlayerSettingPermissions({ appSettingId: setting.id, page, perPage: 25 })
      .then(response => {
        setPermissions(response.content);
        setTotalPages(response.pagination.totalPages);
        setTotalPermissions(
          response.pagination.totalCount ?? setting.playerPermissionsCount ?? response.content.length
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [setting, page]);

  const permissionUpdates = useMemo(() => {
    if (!setting) return [];

    return Object.values(selectedPermissions).map(permission => ({
      appSettingId: setting.id,
      accountId: permission.accountId,
      value: null,
    })) as AppSettingPermissionUpdate[];
  }, [setting, selectedPermissions]);

  const removePlayerPermissions = useCallback(() => {
    AppSettingServices.updatePlayerSettingPermissions(permissionUpdates)
      .then(() => {
        onClose(true);
      })
      .catch(console.error);
  }, [permissionUpdates, onClose]);

  const confirmRemovePermissions = useCallback(() => {
    if (
      confirm(
        `Are you sure you want to remove the selected ${permissionUpdates.length} ${pluralize(
          "permission",
          permissionUpdates.length
        )}?`
      )
    ) {
      removePlayerPermissions();
    }
  }, [removePlayerPermissions, permissionUpdates.length]);

  const handlePermissionChecked = (checked: boolean, permission: AppSettingPermission) => {
    if (checked) {
      setSelectedPermissions(prev => ({ ...prev, [permission.id]: permission }));
    } else {
      setSelectedPermissions(prev => _.omit(prev, permission.id));
    }
  };

  const someChecked = useMemo(() => {
    return permissions.some(permission => !!selectedPermissions[permission.id]);
  }, [permissions, selectedPermissions]);

  const everyChecked = useMemo(() => {
    return permissions.length > 0 && permissions.every(permission => !!selectedPermissions[permission.id]);
  }, [permissions, selectedPermissions]);

  const handleAllPermissionsChecked = useCallback(() => {
    if (everyChecked) {
      setSelectedPermissions(prev => {
        const newSelectedPermissions = { ...prev };
        permissions.forEach(permission => delete newSelectedPermissions[permission.id]);
        return newSelectedPermissions;
      });
    } else {
      setSelectedPermissions(prev => {
        return {
          ...prev,
          ...permissions.reduce((acc, permission) => ({ ...acc, [permission.id]: permission }), {}),
        };
      });
    }
  }, [everyChecked, permissions]);

  const cancel = () => onClose(false);

  return (
    <Modal open={!!setting} onClose={cancel} className="playerPermissionsModal">
      <Modal.Header>Player Permissions</Modal.Header>
      <Modal.Content>
        <div>
          The following Players have overrides for the <b>{setting?.name}</b> setting.
          {withWritePermission && (
            <>
              <br />
              Select which ones you would like to remove.
            </>
          )}
        </div>
        <Table compact celled definition={withWritePermission}>
          <Table.Header fullWidth>
            <Table.Row disabled={loading}>
              {withWritePermission && (
                <Table.HeaderCell>
                  <Checkbox
                    checked={someChecked}
                    indeterminate={someChecked && !everyChecked}
                    onChange={handleAllPermissionsChecked}
                  />
                </Table.HeaderCell>
              )}
              <Table.HeaderCell>Player</Table.HeaderCell>
              <Table.HeaderCell>Enabled?</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {permissions.map(permission => (
              <Table.Row key={permission.id} disabled={loading}>
                {withWritePermission && (
                  <Table.Cell collapsing>
                    <Checkbox
                      checked={!!selectedPermissions[permission.id]}
                      onChange={(_, data) => handlePermissionChecked(!!data.checked, permission)}
                    />
                  </Table.Cell>
                )}
                <Table.Cell>
                  <a target="_blank" href={URLS.coach.editPlayer.replace(":playerId", `${permission.account.id}`)}>
                    {permission.account.fullName}
                  </a>
                </Table.Cell>
                <Table.Cell>{permission.value ? "Yes" : "No"}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        {totalPages > 1 && (
          <Pagination
            activePage={page}
            totalPages={totalPages}
            firstItem={null}
            lastItem={null}
            onPageChange={(_, data) => setPage(data.activePage as number)}
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        {withWritePermission && (
          <Button
            secondary
            onClick={confirmRemovePermissions}
            disabled={!permissionUpdates.length}
            content={`Remove ${permissionUpdates.length}${totalPages > 1 ? `/${totalPermissions}` : ""} ${pluralize(
              "Permissions",
              permissionUpdates.length
            )}`}
          />
        )}
        <Button primary onClick={cancel}>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default PlayerPermissionsModal;
